import { animated, useSpring, useSprings } from '@react-spring/web';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { MediaAssets } from '../services/ofertas-service/models/ofertas';

const CarrouselContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	position: relative;
`;

const CarImagesContainer = styled.div`
	width: 100vw;
	overflow: hidden;
	height: 100%;
	position: relative;
	z-index: 0;
`;

const StyledCarImagesRow = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	position: relative;
	height: 100%;
	white-space: nowrap;
	pointer-events: none;
`;

const StyledCarImage = styled.img`
	height: 100%;
	flex-shrink: 0;
	object-fit: contain;
	width: clamp(150px, 42vw, 925px);

	@media screen and (min-width: 962px) and (max-width: 1199px) {
		height: 265px;
	}
`;
const ControlsContainer = styled.div`
	position: absolute;
	bottom: -50px;
	left: 40%;
	transform: translateX(-50%);
	z-index: 10;
	pointer-events: auto;

	@media screen and (min-width: 960px) and (max-width: 1199px) {
		bottom: 20%;
		left: 50%;
	}
`;

const PauseIcon = () => (
	<svg
		width="12"
		height="12"
		viewBox="0 0 12 12"
		xmlns="http://www.w3.org/2000/svg"
	>
		<rect x="2" y="2" width="2.5" height="8" rx="0.5" fill="white" />
		<rect x="7.5" y="2" width="2.5" height="8" rx="0.5" fill="white" />
	</svg>
);

const PlayIcon = () => (
	<svg
		width="12"
		height="12"
		viewBox="0 0 12 12"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path d="M3 2L10 6L3 10V2Z" fill="white" />
	</svg>
);

const IconWrapper = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: #3c484d;
	border-radius: 50%;
	width: 24px;
	height: 24px;
	flex-shrink: 0;
`;

const PlayPauseButton = styled.button<{ $paused: boolean }>`
	min-width: 101px;
	height: 40px;
	background-color: #6a767d;
	color: #fff;
	border: none;
	border-radius: 24px;
	padding: 8px 12px;
	font-size: 14px;
	font-weight: 500;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
	cursor: pointer;
	opacity: 1;

	white-space: nowrap;
	overflow: visible;
	transition:
		background-color 0.18s ease,
		width 0.18s ease,
		padding 0.18s ease;
	box-sizing: border-box;

	&:hover {
		background-color: #2f383c;
	}
`;

const CarImagesRow = animated(StyledCarImagesRow);
const CarImage = animated(StyledCarImage);

export const Carrousel = ({
	mediaAssets,
}: {
	mediaAssets: MediaAssets;
}): React.ReactElement => {
	const turntable = [
		'D6Turntable1',
		'D6Turntable3',
		'D6Turntable5',
		'D6Turntable6',
		'D6Turntable7',
		'D6Turntable9',
		'D6Turntable12',
	];

	const carImages = turntable
		.map((t) => mediaAssets[t as keyof MediaAssets])
		.filter(
			(image): image is string => typeof image === 'string' && image.length > 0,
		);

	const loopImages = [...carImages, ...carImages, ...carImages];
	const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
	const [imageWidth, setImageWidth] = useState(0);
	const imageRef = useRef<HTMLImageElement | null>(null);
	const [isPaused, setIsPaused] = useState(false);

	useEffect(() => {
		const handleResize = () => setWindowWidth(window.innerWidth);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		if (!imageRef.current || typeof ResizeObserver === 'undefined') return;

		const observer = new ResizeObserver((entries) => {
			const width = entries[0].contentRect.width;
			setImageWidth(width);
		});

		observer.observe(imageRef.current);
		return () => observer.disconnect();
	}, []);

	const [{ x }, carrousel] = useSpring(() => ({
		from: { x: 0 },
		to: { x: 0 },
		config: { duration: 120000 },
	}));

	useEffect(() => {
		if (!imageWidth || carImages.length === 0) return;

		const startIndex = carImages.length;
		const initialOffset =
			-startIndex * imageWidth + (windowWidth / 2 - imageWidth / 2 - 120);
		const targetOffset = initialOffset - imageWidth * carImages.length;

		carrousel.start({
			from: { x: initialOffset },
			to: { x: targetOffset },
			loop: true,
		});

		return () => {
			carrousel.stop();
		};
	}, [carrousel, imageWidth, windowWidth, carImages.length]);

	useEffect(() => {
		if (isPaused) {
			carrousel.pause();
		} else {
			carrousel.resume();
		}
	}, [isPaused, carrousel]);

	const menuWidth = 250;
	const center = windowWidth / 2;
	const fadeStart = center - menuWidth - imageWidth / 2;
	const fadeEnd = fadeStart + 150;

	const springs = useSprings(
		loopImages.length,
		loopImages.map((_, i) => ({
			opacity: x.to((val) => {
				const imageCenterX = i * imageWidth + val + imageWidth / 2;
				const distance = center - imageCenterX;
				if (distance < fadeStart) return 1;
				if (distance > fadeEnd) return 0.25;
				const fadeRange = fadeEnd - fadeStart;
				const relative = (distance - fadeStart) / fadeRange;
				return 1 - relative * 0.75;
			}),
		})),
	);

	return (
		<CarrouselContainer>
			<CarImagesContainer>
				<CarImagesRow
					style={{
						width: `${loopImages.length * imageWidth}px`,
						transform: x.to((val) => `translateX(${val}px)`),
					}}
				>
					{loopImages.map((image, index) => (
						<CarImage
							key={index}
							ref={index === 0 ? imageRef : null}
							src={image}
							alt={`Imagen del coche ${index + 1}`}
							style={{ opacity: springs[index].opacity }}
						/>
					))}
				</CarImagesRow>
			</CarImagesContainer>

			<ControlsContainer>
				<PlayPauseButton
					$paused={isPaused}
					onClick={() => setIsPaused(!isPaused)}
					aria-pressed={isPaused}
					aria-label={isPaused ? 'Reproducir animación' : 'Detener animación'}
				>
					<IconWrapper>{isPaused ? <PlayIcon /> : <PauseIcon />}</IconWrapper>
					{isPaused ? 'Reproducir' : 'Detener'}
				</PlayPauseButton>
			</ControlsContainer>
		</CarrouselContainer>
	);
};
